import { CommonModule } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, Injector, NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NotFoundPageModule } from "@gloria-public/error/views/not-found-page/not-found-page.module";
import { HomePageModule } from "@gloria-public/home/views/home-page/home-page.module";
import { LandingPageModule } from "@gloria-public/landing/views/landing-page/landing-page.module";
import { LoginFormModule } from "@gloria-public/login/views/components/login-form/login-form.module";
import { ResendVerifyModule } from "@gloria-public/login/views/components/resend-verify/resend-verify.module";
import { ResetPasswordFormModule } from "@gloria-public/login/views/components/reset-password-form/reset-password-form.module";
import { LoginDialogModule } from "@gloria-public/login/views/login-dialog/login-dialog.module";
import { LoginPageModule } from "@gloria-public/login/views/login-page/login-page.module";
import { ResetPasswordDialogModule } from "@gloria-public/login/views/reset-password-dialog/reset-password-dialog.module";
import { ResetPasswordPageModule } from "@gloria-public/login/views/reset-password-page/reset-password-page.module";
import { OrderModule } from "@gloria-public/order/views/order.module";
import { QuestionnairesModule } from "@gloria-public/questionnaire/views/questionnaires.module";
import { ExpiredValidationComponent } from "@gloria-public/register/views/link-page/components/expired-validation/expired-validation.component";
import { LinkPageComponent } from "@gloria-public/register/views/link-page/link-page.component";
import { RegistrationPageModule } from "@gloria-public/register/views/registration-page/registration-page.module";
import { RisksModule } from "@gloria-public/risk/views/risks.module";
import { TranslateService } from "@ngx-translate/core";
import { AddToCartErrorModule } from "@shared/components/add-to-cart-error/add-to-cart-error.module";
import { ChatButtonComponent } from "@shared/components/chat-button/chat-button.component";
import { ConsentDialogModule } from "@shared/components/consent-dialog/consent-dialog.module";
import { FooterComponent } from "@shared/components/footer/footer.component";
import { HeaderComponent } from "@shared/components/header/header.component";
import { SearchRiskTagsModule } from "@shared/components/search-risk-tags/search-risk-tags.module";
import { ServiceRequestDialogModule } from "@shared/components/service-request-dialog/service-request-dialog.module";
import { ConfigService } from "@shared/services/config.service";
import { SessionService } from "@shared/services/session.service";
import { CommonTranslateModule } from "@shared/utils/common-translate.module";
import { firstValueFrom } from "rxjs";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";

const materialModules = [
  MatButtonModule,
  MatIconModule,
  MatMenuModule,
  MatSelectModule,
  MatProgressSpinnerModule,
  MatToolbarModule,
  MatTooltipModule,
  MatDialogModule,
];

/**
 * Factory for the `APP_INITIALIZER` provider that boots the app's core async dependencies
 * in a strict, guaranteed order.
 *
 * Order matters here: `TranslateService` (and its loader chain, `CustomI18nTranslateLoader`
 * → `LanguageService` → `ApiService`) must not be constructed until `ConfigService.load()`
 * has fully resolved, since `ApiService` relies on config values (e.g. `apiUrl`) being
 * available.
 *
 * To achieve this, `TranslateService` and `SessionService` are intentionally **not** listed
 * in `deps`. Angular resolves all `deps` synchronously when building the list of
 * `APP_INITIALIZER` factories, which would instantiate `TranslateService` (and therefore
 * `ApiService`) immediately — before `configService.load()` has finished, even though this
 * factory itself runs later. Instead, only the generic `Injector` is injected, and the
 * actual dependent services are resolved manually via `injector.get(...)` inside the async
 * function body, strictly after the `await configService.load()` call. This guarantees the
 * config is fully loaded before `TranslateService` (and its dependency chain) is created.
 *
 * @param configService - Service responsible for loading app configuration (e.g. `apiUrl`)
 *   before anything else that depends on it is instantiated.
 * @param injector - The root `Injector`, used to lazily resolve `TranslateService` and
 *   `SessionService` only after config has loaded, avoiding premature instantiation of their
 *   dependency chain.
 * @returns A factory function invoked by Angular's `APP_INITIALIZER` mechanism. The returned
 *   promise resolves once both config loading and the initial translation setup are complete;
 *   Angular delays app bootstrap until it resolves.
 */
function initApp(configService: ConfigService, injector: Injector): () => Promise<void> {
  return async () => {
    // Step 1: load app configuration first (e.g. apiUrl) — everything else depends on it.
    await configService.load();

    // Step 2: only now resolve TranslateService, which lazily triggers construction of
    // CustomI18nTranslateLoader -> LanguageService -> ApiService, guaranteeing config
    // is already available by the time ApiService is built.
    const translateService = injector.get(TranslateService);
    const sessionService = injector.get(SessionService);

    translateService.setDefaultLang("en"); // fallback language when a translation key is missing
    await firstValueFrom(translateService.use(sessionService.userLang));
  };
}

@NgModule({
  declarations: [AppComponent, HeaderComponent, FooterComponent, ChatButtonComponent, LinkPageComponent, ExpiredValidationComponent],
  imports: [
    CommonModule,
    NotFoundPageModule,
    HomePageModule,
    LoginPageModule,
    LandingPageModule,
    LoginFormModule,
    LoginDialogModule,
    ResendVerifyModule,
    ResetPasswordDialogModule,
    ResetPasswordFormModule,
    ResetPasswordPageModule,
    RegistrationPageModule,
    ConsentDialogModule,
    QuestionnairesModule,
    OrderModule,
    AddToCartErrorModule,
    RisksModule,
    SearchRiskTagsModule,
    ServiceRequestDialogModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,
    CommonTranslateModule,
    ...materialModules,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [ConfigService, Injector],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
